import {
  Component, ComponentRef, EventEmitter, Input, OnChanges, OnDestroy, Output, Renderer2, SimpleChanges, ViewChild, ViewContainerRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { WidgetBase } from '../widget-base';
import { WidgetContexto, WidgetItem } from '../widget-contexto';
import { WIDGET_REGISTRY } from '../widget-registry';

/**
 * Quadro de um widget no dashboard (FRONTEND.md §4.7): cabeçalho, X e alça de arraste no modo edição,
 * criação dinâmica do componente pelo registro e "Widget indisponível".
 */
@Component({
  selector: 'lx-widget-host',
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule],
  templateUrl: './widget-host.component.html',
  styleUrl: './widget-host.component.css'
})
export class WidgetHostComponent implements OnChanges, OnDestroy {

  @Input({ required: true }) item!: WidgetItem;
  @Input() modoEdicao = false;
  @Input() mobile = false;

  @Output() configChange = new EventEmitter<Record<string, unknown>>();
  @Output() remover = new EventEmitter<void>();
  /** pointerdown no cabeçalho, no modo edição (o arraste é controlado pelo dashboard). */
  @Output() arrastar = new EventEmitter<PointerEvent>();

  @ViewChild('alvo', { read: ViewContainerRef, static: true }) alvo!: ViewContainerRef;

  indisponivel = false;
  falhaCarregamento = false;
  carregandoComponente = false;
  /** O widget tem `configuravel = true` (mostra o botão "Configurar"). */
  configuravel = false;

  private ref?: ComponentRef<WidgetBase<any>>;
  private identificadorCriado?: string;
  private contextoAtual?: string;
  private configEnviado: unknown;
  private destruido = false;
  private readonly destroy$ = new Subject<void>();

  constructor(private renderer: Renderer2) { }

  /** Título personalizado salvo no config do widget (`config.titulo`) ou o nome do cadastro. */
  get titulo(): string {
    const personalizado = this.item.config?.['titulo'];
    return typeof personalizado === 'string' && personalizado.trim() ? personalizado : this.item.nome;
  }

  configurar() {
    this.ref?.instance.abrirConfiguracao();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.item.identificador !== this.identificadorCriado) {
      this.criar();
      return;
    }
    if (this.ref) {
      this.atualizarEntradas();
    }
  }

  async criar(): Promise<void> {
    this.limpar();
    this.identificadorCriado = this.item.identificador;
    this.indisponivel = false;
    this.falhaCarregamento = false;

    const loader = WIDGET_REGISTRY[this.item.identificador];
    if (!loader) {
      this.indisponivel = true;
      return;
    }

    this.carregandoComponente = true;
    try {
      const tipo = await loader();
      if (this.destruido || this.identificadorCriado !== this.item.identificador) return;

      this.ref = this.alvo.createComponent(tipo);
      this.renderer.addClass(this.ref.location.nativeElement, 'lx-widget');
      this.configuravel = this.ref.instance.configuravel === true;
      this.atualizarEntradas();
      this.ref.instance.configChange
        .pipe(takeUntil(this.destroy$))
        .subscribe(cfg => {
          this.configEnviado = cfg;
          this.configChange.emit(cfg as Record<string, unknown>);
        });
    } catch (erro) {
      console.error(`Falha ao carregar o widget "${this.item.identificador}"`, erro);
      this.falhaCarregamento = true;
    } finally {
      this.carregandoComponente = false;
    }
  }

  /** No modo edição, o cabeçalho inteiro serve de alça, exceto o botão de remover. */
  aoPressionarCabecalho(evento: PointerEvent) {
    if (!this.modoEdicao || (evento.target as Element).closest('button')) return;
    this.arrastar.emit(evento);
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.limpar();
  }

  /** Atualiza `contexto` e `config` do componente sem recriá-lo. */
  private atualizarEntradas() {
    const contexto: WidgetContexto = {
      id: this.item.id,
      largura: this.item.largura,
      altura: this.item.altura,
      modoEdicao: this.modoEdicao,
      mobile: this.mobile,
    };
    const chave = JSON.stringify(contexto);
    if (chave !== this.contextoAtual) {
      this.contextoAtual = chave;
      this.ref!.setInput('contexto', contexto);
    }
    // não devolve ao widget o config que ele mesmo acabou de emitir
    if (this.item.config !== this.configEnviado) {
      this.configEnviado = this.item.config;
      this.ref!.setInput('config', this.item.config);
    }
  }

  private limpar() {
    this.destroy$.next();
    this.alvo?.clear();
    this.ref = undefined;
    this.configuravel = false;
    this.contextoAtual = undefined;
    this.configEnviado = undefined;
  }
}
